//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getRightsConfigurations } from '@apis/rights-management/rightsManagementApi';
import { RightsConfigurationsResponse } from '@apis/rights-management/rightsManagementApiModels';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import ClientId from '@models/ClientId';
import PartialCuid from '@models/PartialCuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { getClientId } from '@redux/selectors/getClientId';
import { getLaunchOptions } from '@uia/redux/uiaSelector';
import { isDefined } from '@utils/filterPredicates';

const TARGET_CLIENT_ID = ClientId(2202);
const VENDOR_UGC_CONTAINER_ID = 'Zh3aRrw3';
const NON_CONNECT_CONFIG_IDS = [12, 1585, 6804, 7269];

export const useRightsConfigurationsQuery = (
  cuid?: PartialCuid,
  requestType?: RightsRequestOptions,
) => {
  const clientId = useSelector(getClientId);
  const launchOptions = useSelector(getLaunchOptions);
  const reelId = launchOptions?.selectedReel?.id;

  return useQuery<RightsConfigurationsResponse>({
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsRightsConfigurations,
      cuid,
      requestType,
      clientId,
      reelId,
    ],
    queryFn: async ({ signal }) => {
      const configs = await getRightsConfigurations(
        {
          networkItemId:
            requestType === RightsRequestOptions.COMMENTS ? cuid : undefined,
        },
        { signal },
      );
      // Target has a specific gallery called Vendor UGC
      // if the client is Target and the selected gallery is not for the Vendor UGC reel, then filter down to only the rights configurations that are not for connect
      // if the client is Target and the selected reel is not the vendor reel, then filter out Connect Vendor rights configs
      if (
        clientId === TARGET_CLIENT_ID &&
        isDefined(reelId) &&
        reelId !== VENDOR_UGC_CONTAINER_ID
      ) {
        return configs.filter(
          (config) =>
            isDefined(config.id) && NON_CONNECT_CONFIG_IDS.includes(config.id),
        );
      }

      return configs;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};
